import React, { useState, useEffect, useCallback } from 'react';
import * as Styles from './GatorBackupHandler.styles';
import useLocale from '@/hooks/useLocale';
import Snappy from './media/snappy.svg';
import {
  GATOR_BACKUP_PLANS, GATOR_BACKUP_PLANS_IDS, GATOR_BACKUP_TYPE, HIGHLIGHT_ID, PLAN_CYCLE_TYPES,
} from './GatorBackup.enum';
import IconAlert from './media/icon-alerts.svg';
import IconBackupAuto from './media/icon-backup-auto.svg';
import IconBackupCloud from './media/icon-backup-cloud.svg';
import {
  Button, SelectField, Skeleton, TextField, Toggle,
} from 'gatorcomponents';
import { domainsActions } from '@/redux/modules/domains';
import { useDispatch, useSelector } from 'react-redux';
import { gatorBackupActions, homeActions } from '@/redux/modules';
import { isBRServer } from '@/utils/Validators/validation';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import GradientButton from '@/components/Buttons/GradientButton';
import { useHistory } from 'react-router';

const GatorBackupHandler = () => {
  const { gatorBackup: gatorBackupLocale, routes: routesLocale } = useLocale();
  const [planCycle, setPlanCycle] = useState(PLAN_CYCLE_TYPES.ANNUALLY);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [useSelectField, setUseSelectField] = useState(true);
  const [inputFocus, setInputFocus] = useState(false);
  const [clickedPlan, setClickedPlan] = useState(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const { list: domainsList, loading: domainsLoading } = useSelector(state => state.domains);
  const { productsPriceData: productsOfferData, loading: productsPriceLoading } = useSelector(state => state.home);
  const buyInvoiceId = useSelector(state => state.gatorBackup && state.gatorBackup.buy && state.gatorBackup.buy.invoiceId);
  const isLoadingBuy = useSelector(state => state.gatorBackup && state.gatorBackup.loading);

  const getProductsPriceList = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.prices)) return raw.prices;
    return [];
  };

  const getPriceByProduct = (apiType) => {
    if (!apiType) return undefined;
    const entry = getProductsPriceList(productsOfferData).find(item => item.type === apiType);
    const price = entry?.cycles?.[planCycle]?.price?.discounts?.[0]?.per_month;
    return price ? formatCurrency(price).replace(gatorBackupLocale.tablePlans.priceSymbol, '').replace(' ', '') : undefined;
  };

  const getPricePerDay = (apiType) => {
    if (!apiType) return undefined;
    const entry = getProductsPriceList(productsOfferData).find(item => item.type === apiType);
    const pricePerDay = (entry?.cycles?.[planCycle]?.price?.discounts?.[0]?.per_month / 30).toFixed(2);
    return pricePerDay ? formatCurrency(pricePerDay) : '';
  };

  const isValidDomain = useCallback((domain) => {
    if (!domain) return false;
    if (/^www\./i.test(domain)) return false;

    return /^([a-zA-Z0-9\u00C0-\u024F\u1E00-\u1EFF-]{1,63}\.)+[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF]{2,}$/u.test(domain);
  }, []);

  const getDomains = useCallback(() => {
    dispatch(domainsActions.domains.request());
  }, [dispatch]);

  const getPrices = useCallback(() => {
    const brand = isBRServer ? 'BR' : 'ES';

    dispatch(homeActions.userProductsPrice.request({
      [GATOR_BACKUP_PLANS.CORE]: {
        pid: GATOR_BACKUP_PLANS_IDS[brand].CORE,
        type: GATOR_BACKUP_TYPE[brand],
        discount: 0,
      },
      [GATOR_BACKUP_PLANS.MAX]: {
        pid: GATOR_BACKUP_PLANS_IDS[brand].MAX,
        type: GATOR_BACKUP_TYPE[brand],
        discount: 0,
      },
      [GATOR_BACKUP_PLANS.PLUS]: {
        pid: GATOR_BACKUP_PLANS_IDS[brand].PLUS,
        type: GATOR_BACKUP_TYPE[brand],
        discount: 0,
      },
      [GATOR_BACKUP_PLANS.ADVANCED]: {
        pid: GATOR_BACKUP_PLANS_IDS[brand].ADVANCED,
        type: GATOR_BACKUP_TYPE[brand],
        discount: 0,
      },
    }));
  }, [dispatch]);

  const handlePasteOnPage = useCallback((event) => {
    const pastedValue = event.clipboardData?.getData('text');
    const sanitizedValue = pastedValue.replace(/^https?:\/\//i, '').replace(/[^a-zA-Z0-9\-.]/g, '');
    setSelectedDomain(sanitizedValue);
  }, []);

  const handleSelectPlan = useCallback((planKey) => {
    if (isLoadingBuy) return;

    const brand = isBRServer ? 'BR' : 'ES';
    const pidByPlan = {
      [GATOR_BACKUP_PLANS.CORE]: GATOR_BACKUP_PLANS_IDS[brand].CORE,
      [GATOR_BACKUP_PLANS.PLUS]: GATOR_BACKUP_PLANS_IDS[brand].PLUS,
      [GATOR_BACKUP_PLANS.ADVANCED]: GATOR_BACKUP_PLANS_IDS[brand].ADVANCED,
      [GATOR_BACKUP_PLANS.MAX]: GATOR_BACKUP_PLANS_IDS[brand].MAX,
    };

    setClickedPlan(planKey);
    dispatch(gatorBackupActions.buy.request({
      id: pidByPlan[planKey],
      cycle: planCycle,
      domain: selectedDomain,
      quantity: 1,
    }));
  }, [isLoadingBuy, planCycle, selectedDomain, dispatch]);

  useEffect(() => {
    if (!isLoadingBuy && clickedPlan) {
      setClickedPlan(null);
    }
  }, [isLoadingBuy, clickedPlan]);

  useEffect(() => {
    getDomains();
    getPrices();
  }, [getDomains, getPrices]);

  useEffect(() => {
    if (buyInvoiceId) {
      history.push(`${routesLocale.billing}${routesLocale.unpaid}/${buyInvoiceId}?openInvoice=true`);
    }
  }, [buyInvoiceId, history, routesLocale.billing, routesLocale.unpaid]);

  useEffect(() => {
    document.addEventListener('paste', handlePasteOnPage);

    return () => {
      document.removeEventListener('paste', handlePasteOnPage);
    };
  }, [handlePasteOnPage]);

  const getIcon = id => ({
    [HIGHLIGHT_ID.BACKUP_ALERT]: IconAlert,
    [HIGHLIGHT_ID.BACKUP_AUTO]: IconBackupAuto,
    [HIGHLIGHT_ID.BACKUP_CLOUD]: IconBackupCloud,
  })[id];

  return (
    <Styles.Wrapper>
      <Styles.Content>
        <Styles.ShapeBlue />
        <Styles.ShapeGreen />
        <Styles.Header>
          <Styles.Title>{gatorBackupLocale.title}</Styles.Title>
          <Styles.Description>{gatorBackupLocale.description}</Styles.Description>
          <Styles.SnappyImage src={Snappy} alt="Snappy" />
        </Styles.Header>

        <Styles.WrapperHighlights>
          <Styles.Highlight>
            {gatorBackupLocale.highlights.map(highlight => (
              <Styles.HighlightItem key={highlight.title}>
                <Styles.HighlightItemIcon src={getIcon(highlight.id)} alt={highlight.title} />
                <Styles.HighlightItemTitle>{highlight.title}</Styles.HighlightItemTitle>
                <Styles.HighlightItemDescription>{highlight.description}</Styles.HighlightItemDescription>
              </Styles.HighlightItem>
            ))}
          </Styles.Highlight>
        </Styles.WrapperHighlights>

        <Styles.WrapperFields>
          <Styles.FieldRow>
            <Styles.FieldLabel htmlFor="domain">{gatorBackupLocale.domain.label}</Styles.FieldLabel>

            <Styles.WrapperSelectField>
              <Styles.ToggleWrapper>
                <Toggle
                  onMark={() => !isLoadingBuy && setUseSelectField(!useSelectField)}
                  testId="Toggle"
                  checked={!useSelectField}
                  disabled={isLoadingBuy}
                />
                <Styles.ToggleLabel>{gatorBackupLocale.domain.toggleLabel}</Styles.ToggleLabel>
              </Styles.ToggleWrapper>

              {domainsLoading ? (
                <Skeleton variant="input" height="58px" width="100%" />
              ) : (
                <>
                  {useSelectField ? (
                    <SelectField
                      placeholder={gatorBackupLocale.domain.placeholder}
                      options={domainsList.map(domain => ({ label: domain.domain, value: domain.id }))}
                      value={selectedDomain?.label}
                      onClickSelect={option => setSelectedDomain(option.label)}
                      size="md"
                      disabled={isLoadingBuy}
                    />
                  ) : (
                    <TextField
                      label={gatorBackupLocale.domain.textFieldPlaceholder}
                      value={typeof selectedDomain === 'string' ? selectedDomain : ''}
                      onChange={e => setSelectedDomain(e.target.value)}
                      status={selectedDomain && typeof selectedDomain === 'string' && !isValidDomain(selectedDomain) ? 'error' : 'default'}
                      helpText={!inputFocus && selectedDomain && !isValidDomain(selectedDomain) ? gatorBackupLocale.domain.errorMessage : ''}
                      mask="noSpecialCharacters"
                      size="md"
                      onFocusInput={() => setInputFocus(true)}
                      onBlur={() => setInputFocus(false)}
                      disabled={isLoadingBuy}
                    />
                  )}
                </>
              )}
            </Styles.WrapperSelectField>
          </Styles.FieldRow>

          <Styles.FieldRow>
            <Styles.FieldLabel htmlFor="plan">{gatorBackupLocale.plan.label}</Styles.FieldLabel>

            <Styles.WrapperOptionsPlan>
              {Object.entries(gatorBackupLocale.plan.options).map(([key, value]) => (
                <Styles.OptionPlan key={key} onClick={() => !isLoadingBuy && setPlanCycle(key)} active={planCycle === key}>
                  <Styles.OptionPlanIcon active={planCycle === key} />
                  <Styles.OptionPlanLabel>{value}</Styles.OptionPlanLabel>
                </Styles.OptionPlan>
              ))}
            </Styles.WrapperOptionsPlan>
          </Styles.FieldRow>
        </Styles.WrapperFields>

        <Styles.WrapperPlans>
          {Object.entries(gatorBackupLocale.tablePlans.options).map(([key, value]) => (
            <Styles.PlanCard key={key} active={GATOR_BACKUP_PLANS.ADVANCED === key}>
              <Styles.PlanCardInner>
                {GATOR_BACKUP_PLANS.ADVANCED === key && (
                  <Styles.PlanCardBadge>{gatorBackupLocale.tablePlans.badge}</Styles.PlanCardBadge>
                )}

                <Styles.PlanCardHeader>
                  <Styles.PlanCardTitle>{value.name}</Styles.PlanCardTitle>

                  <Styles.WrapperPrice isLoading={productsPriceLoading || !getPriceByProduct(key)}>
                    {productsPriceLoading || !getPriceByProduct(key) ? (
                      <Skeleton variant="headline" height="42px" width="80%" />
                    ) : (
                      <>
                        <Styles.ProductSmallText>{gatorBackupLocale.tablePlans.priceSymbol}</Styles.ProductSmallText>
                        <Styles.ProductPrice>{getPriceByProduct(key)}</Styles.ProductPrice>
                        <Styles.ProductSmallText>{gatorBackupLocale.tablePlans.priceSuffix}</Styles.ProductSmallText>
                      </>
                    )}
                  </Styles.WrapperPrice>

                  <Styles.ProductPricePerDay>
                    {productsPriceLoading || !getPriceByProduct(key) ? (
                      <Skeleton variant="textLine" height="18px" width="60px" />
                    ) : (
                      <>{gatorBackupLocale.tablePlans.pricePerDay(getPricePerDay(key))}</>
                    )}
                  </Styles.ProductPricePerDay>

                  <Styles.PlanCardCta>
                    {GATOR_BACKUP_PLANS.ADVANCED === key ? (
                      <GradientButton
                        disabled={!isValidDomain(selectedDomain) || (isLoadingBuy && clickedPlan !== key)}
                        loading={clickedPlan === key && isLoadingBuy}
                        variant="twilight"
                        size="md"
                        label={value.cta}
                        width="full"
                        onClick={() => handleSelectPlan(key)}
                      />
                    ) : (
                      <Button
                        variant="secondary"
                        size="large"
                        label={value.cta}
                        version="v2"
                        width="full"
                        loading={clickedPlan === key && isLoadingBuy}
                        disabled={!isValidDomain(selectedDomain) || (isLoadingBuy && clickedPlan !== key)}
                        title={!isValidDomain(selectedDomain) ? gatorBackupLocale.tablePlans.ctaTitleValidation : ''}
                        onClick={() => handleSelectPlan(key)}
                      />
                    )}
                  </Styles.PlanCardCta>
                </Styles.PlanCardHeader>

                <Styles.PlanCardBody>
                  <Styles.PlanCardText>{gatorBackupLocale.tablePlans.advantages}</Styles.PlanCardText>
                  <Styles.PlanCardHighlights>
                    {Object.entries(value.highlights).map(([key, highlight]) => (
                      <Styles.PlanCardHighlightItem key={key}>
                        <Styles.PlanCardHighlightText>
                          {highlight}
                        </Styles.PlanCardHighlightText>
                      </Styles.PlanCardHighlightItem>
                    ))}
                  </Styles.PlanCardHighlights>
                </Styles.PlanCardBody>
              </Styles.PlanCardInner>
            </Styles.PlanCard>
          ))}
        </Styles.WrapperPlans>
      </Styles.Content>
    </Styles.Wrapper>
  );
};

export default GatorBackupHandler;
